import apiClient from './index';

export type FieldStaffRole = 'installer' | 'pnr_engineer' | 'field_engineer';

export interface FieldStaff {
  id: string;
  email: string;
  full_name?: string | null;
  role: FieldStaffRole;
  is_active: boolean;
  created_at: string;
}

export interface InstallationWorkspace {
  id: string;
  name: string;
  schema_name: string;
  workspace_kind: string;
  lifecycle_status: string;
}

export interface StaffBuildingOption {
  id: string;
  name: string;
  address?: string | null;
}

export interface WorkspaceStructureProgress {
  building_id: string;
  building_name: string;
  building_address?: string | null;
  plan_id?: string | null;
  status: 'not_started' | 'draft' | 'applied';
  revision: number;
  corps: number;
  entrances: number;
  floors: number;
  apartments: number;
  conflicts: number;
  updated_at?: string | null;
  updated_by_user_id?: string | null;
  updated_by_name?: string | null;
}

export interface StaffAccess {
  id: string;
  user_id: string;
  tenant_id: string;
  tenant_name: string;
  tenant_schema: string;
  access_role: string;
  access_level: 'read' | 'write';
  expires_at?: string | null;
  revoked_at?: string | null;
  building_ids: string[];
}

export const fieldStaffApi = {
  async listStaff(): Promise<FieldStaff[]> {
    return (await apiClient.get<FieldStaff[]>('/admin/field-staff')).data;
  },

  async createStaff(payload: {
    email: string;
    password: string;
    full_name: string;
    role: FieldStaffRole;
  }): Promise<FieldStaff> {
    return (await apiClient.post<FieldStaff>('/admin/field-staff', payload)).data;
  },

  async updateStaff(
    userId: string,
    payload: {
      full_name?: string;
      role?: FieldStaffRole;
      is_active?: boolean;
      password?: string;
    },
  ): Promise<FieldStaff> {
    return (
      await apiClient.patch<FieldStaff>(`/admin/field-staff/${userId}`, payload)
    ).data;
  },

  async deleteStaff(userId: string): Promise<void> {
    await apiClient.delete(`/admin/field-staff/${userId}`);
  },

  async listWorkspaces(): Promise<InstallationWorkspace[]> {
    return (await apiClient.get<InstallationWorkspace[]>('/admin/workspaces')).data;
  },

  async createWorkspace(payload: {
    name: string;
    schema_name?: string;
    notes?: string | null;
  }): Promise<InstallationWorkspace> {
    return (
      await apiClient.post<InstallationWorkspace>('/admin/workspaces', payload)
    ).data;
  },

  async listBuildings(tenantId: string): Promise<StaffBuildingOption[]> {
    return (
      await apiClient.get<StaffBuildingOption[]>(
        `/admin/workspaces/${tenantId}/buildings`,
      )
    ).data;
  },

  async listStructureProgress(
    tenantId: string,
  ): Promise<WorkspaceStructureProgress[]> {
    return (
      await apiClient.get<WorkspaceStructureProgress[]>(
        `/admin/workspaces/${tenantId}/structure-progress`,
      )
    ).data;
  },

  async listAccess(userId: string): Promise<StaffAccess[]> {
    return (await apiClient.get<StaffAccess[]>(`/admin/staff-access/${userId}`)).data;
  },

  async grantAccess(payload: {
    user_id: string;
    tenant_id: string;
    access_level: 'read' | 'write';
    building_ids: string[];
  }): Promise<StaffAccess> {
    return (await apiClient.post<StaffAccess>('/admin/staff-access', payload)).data;
  },

  async revokeAccess(accessId: string): Promise<void> {
    await apiClient.delete(`/admin/staff-access/${accessId}`);
  },
};
